import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import axios from "axios";
import {accessTokenKey, authRedirectReason, refreshTokenKey, usernameKey} from "../core/AuthInterceptor";
import {useOnMount} from "../hooks/UseOnMount";
import {useSnackbar} from "./SnackbarContext";
import SessionExpiredDialog from "../components/auth/SessionExpiredDialog";
import {deleteCookie, getCookie, setCookie} from "../utils/cookies";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import {usersService} from "../services/users.service";
import {CurrentUser, USER_ROLES} from "../models/users.models";
import {authService} from "../services/auth.service";

interface AuthContextType {
    isAuthenticated: boolean;
    isAdmin: boolean;
    isVerifying: boolean;
    currentUserDetails: CurrentUser | null;
    login: (email: string, password: string) => Promise<void>;
    logout: (isManual?: boolean) => void;
}

// --- Context ---

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// --- Provider ---

export const AuthProvider = ({children}: {children: ReactNode}) => {
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();
    const [isAuthenticated, setIsAuthenticated] = useState(() => {
        return !!getCookie(accessTokenKey);
    });
    const [isVerifying, setIsVerifying] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [currentUserDetails, setCurrentUserDetails] = useState<CurrentUser | null>(null);
    const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
    const location = useLocation();

    useOnMount(async () => {
        if (!getCookie(accessTokenKey)) {
            setIsVerifying(false);
            return;
        }

        // fetchLoggedInUser - do not move to service due to need to check status
        try {
            const response = await axios.get(`/users/currentUserDetails`);
            if (response.status >= 200 && response.status <= 300) {
                const userData = response.data;
                setIsAuthenticated(true);
                setIsAdmin(userData.role === 'SYSTEM_ADMIN');
                setCurrentUserDetails(userData);
            }
        } catch {
            deleteCookie(accessTokenKey);
            deleteCookie(refreshTokenKey);
            deleteCookie(usernameKey);
            setCurrentUserDetails(null);
            setIsAdmin(false);
            setIsAuthenticated(false);
        } finally {
            setIsVerifying(false);
        }
    })

    useEffect(() => {
        const handler = () => {
            if (!sessionExpiredOpen) {
                setSessionExpiredOpen(true);
            }
        };
        window.addEventListener('session-expired', handler as any);
        return () => window.removeEventListener('session-expired', handler as any);
    }, [sessionExpiredOpen]);

    const handleSessionExpired = () => {
        setSessionExpiredOpen(false);
        logout(false);
    };

    const login = async (email: string, password: string) => {
        try {
            const data = await authService.loginRequest(email, password);
            const {accessToken, refreshToken, username} = data;

            if (!accessToken || !refreshToken || !username) {
                throw new Error('Invalid response from server');
            }

            setCookie(accessTokenKey, accessToken, 30);
            setCookie(refreshTokenKey, refreshToken, 30);
            setCookie(usernameKey, username, 30);

            localStorage.removeItem(authRedirectReason);

            setIsAuthenticated(true);
            setSessionExpiredOpen(false);

            const userData = await usersService.fetchLoggedInUser();
            setCurrentUserDetails(userData);
            setIsAdmin(userData.role === USER_ROLES.SYSTEM_ADMIN);

            showSnackbar(t('auth.loginSuccess'), 'success');
        } catch (error) {
            deleteCookie(accessTokenKey);
            deleteCookie(refreshTokenKey);
            deleteCookie(usernameKey);
            localStorage.removeItem(authRedirectReason);

            setSessionExpiredOpen(false);
            setIsAuthenticated(false);
            setCurrentUserDetails(null);
            setIsAdmin(false);

            console.error('Error logging in:', error);
            showSnackbar(t('auth.loginError'), 'error');
        }
    };

    const logout = (isManual = true) => {
        deleteCookie(accessTokenKey);
        deleteCookie(refreshTokenKey);
        deleteCookie(usernameKey);

        localStorage.removeItem(authRedirectReason);

        setSessionExpiredOpen(false);
        setIsAuthenticated(false);
        setCurrentUserDetails(null);
        setIsAdmin(false);
        window.location.href = '/login';

        if (isManual) {
            showSnackbar(t('auth.logoutSuccess'), 'success');
        } else {
            showSnackbar(t('auth.sessionExpired'), 'info');
        }
    };

    return (
        <AuthContext.Provider value={{isAuthenticated, login, logout, isVerifying, isAdmin, currentUserDetails}}>
            {children}
            <SessionExpiredDialog
                open={sessionExpiredOpen && !['/login', '/register-admin'].includes(location.pathname)}
                onClose={handleSessionExpired}
            />
        </AuthContext.Provider>
    );
};

// --- Hook ---

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
